// src/widgets/TestControlsWidget.tsx

export interface TestControlsData {
    speed: number;
    vx: number;
    vy: number;
    spin: number;
    tiltX: number;
    onUpdate?: (params: { speed: number; vx: number; vy: number; spin: number; tiltX: number }) => void;
}

export function TestControlsWidget(data?: TestControlsData): HTMLElement {
    const widget = document.createElement('div');
    widget.className = 'widget-content test-controls-widget';

    // Default values
    const defaults = {
        speed: data?.speed ?? 455,
        vx: data?.vx ?? 0,
        vy: data?.vy ?? -455,
        spin: data?.spin ?? 0,
        tiltX: data?.tiltX ?? 0,  // Offset, not absolute tilt
    };

    const title = document.createElement('div');
    title.className = 'widget-title';
    title.textContent = 'Test Controls';
    title.style.cssText = 'font-size: 14px; font-weight: bold; margin-bottom: 12px; color: #fff;';
    widget.appendChild(title);

    const controls = document.createElement('div');
    controls.style.cssText = 'display: flex; flex-direction: column; gap: 12px; padding: 0 8px;';

    // Helper to create a slider control
    const createSlider = (label: string, min: number, max: number, step: number, value: number, onChange: (val: number) => void) => {
        const container = document.createElement('div');
        container.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

        const labelRow = document.createElement('div');
        labelRow.style.cssText = 'display: flex; justify-content: space-between; font-size: 12px; color: #ccc;';

        const labelText = document.createElement('span');
        labelText.textContent = label;

        const valueDisplay = document.createElement('span');
        valueDisplay.textContent = value.toFixed(step >= 1 ? 0 : 2);
        valueDisplay.style.cssText = 'color: #0f0; font-weight: bold; font-family: monospace;';

        labelRow.appendChild(labelText);
        labelRow.appendChild(valueDisplay);

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = min.toString();
        slider.max = max.toString();
        slider.step = step.toString();
        slider.value = value.toString();
        slider.style.cssText = 'width: 100%; cursor: pointer;';

        slider.oninput = () => {
            const val = parseFloat(slider.value);
            valueDisplay.textContent = val.toFixed(step >= 1 ? 0 : 2);
            onChange(val);
        };

        container.appendChild(labelRow);
        container.appendChild(slider);
        return { container, slider, valueDisplay };
    };

    // State
    let currentParams = { ...defaults };

    // Speed slider (200-700 px/s)
    const speedControl = createSlider('Speed (px/s)', 200, 1100, 1, defaults.speed, (val) => {
        currentParams.speed = val;
        updateVelocityFromSpeed();
    });

    // VX slider (-200 to +200 px/s)
    const vxControl = createSlider('VX (lateral)', -200, 200, 1, defaults.vx, (val) => {
        currentParams.vx = val;
        updateSpeedFromVelocity();
    });

    // VY slider (-700 to 0 px/s, negative = upward)
    const vyControl = createSlider('VY (upward)', -700, 0, 1, defaults.vy, (val) => {
        currentParams.vy = val;
        updateSpeedFromVelocity();
    });

    // Spin slider (-0.5 to +0.5)
    const spinControl = createSlider('Spin', -0.5, 0.5, 0.01, defaults.spin, (val) => {
        currentParams.spin = val;
        data?.onUpdate?.(currentParams);
    });

    // Tilt slider (-50 to 0, negative = left tilt)
    const tiltControl = createSlider('Tilt Offset', -40, 10, .1, defaults.tiltX, (val) => {
        currentParams.tiltX = val;
        data?.onUpdate?.(currentParams);
    });

    // Update vy from speed and vx
    function updateVelocityFromSpeed() {
        const speed = currentParams.speed;
        const vx = currentParams.vx;
        const vy = -Math.sqrt(Math.max(0, speed * speed - vx * vx));
        currentParams.vy = vy;
        vyControl.slider.value = vy.toString();
        vyControl.valueDisplay.textContent = vy.toFixed(0);
        data?.onUpdate?.(currentParams);
    }

    // Update speed from vx and vy
    function updateSpeedFromVelocity() {
        const vx = currentParams.vx;
        const vy = currentParams.vy;
        const speed = Math.sqrt(vx * vx + vy * vy);
        currentParams.speed = speed;
        speedControl.slider.value = speed.toString();
        speedControl.valueDisplay.textContent = speed.toFixed(0);
        data?.onUpdate?.(currentParams);
    }

    controls.appendChild(speedControl.container);
    controls.appendChild(vxControl.container);
    controls.appendChild(vyControl.container);
    controls.appendChild(spinControl.container);
    controls.appendChild(tiltControl.container);

    // Reset button
    const resetBtn = document.createElement('button');
    resetBtn.textContent = 'Reset to Defaults';
    resetBtn.style.cssText = `
        padding: 8px 12px;
        background: #444;
        color: #fff;
        border: 1px solid #666;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        margin-top: 4px;
    `;
    resetBtn.onclick = () => {
        currentParams = { ...defaults };
        speedControl.slider.value = defaults.speed.toString();
        speedControl.valueDisplay.textContent = defaults.speed.toFixed(0);
        vxControl.slider.value = defaults.vx.toString();
        vxControl.valueDisplay.textContent = defaults.vx.toFixed(0);
        vyControl.slider.value = defaults.vy.toString();
        vyControl.valueDisplay.textContent = defaults.vy.toFixed(0);
        spinControl.slider.value = defaults.spin.toString();
        spinControl.valueDisplay.textContent = defaults.spin.toFixed(2);
        tiltControl.slider.value = defaults.tiltX.toString();
        tiltControl.valueDisplay.textContent = defaults.tiltX.toFixed(0);
        data?.onUpdate?.(currentParams);
    };
    controls.appendChild(resetBtn);

    widget.appendChild(controls);

    // Expose update method
    (widget as any).updateTestControls = (newData: TestControlsData) => {
        if (newData.speed !== undefined) {
            currentParams.speed = newData.speed;
            speedControl.slider.value = newData.speed.toString();
            speedControl.valueDisplay.textContent = newData.speed.toFixed(0);
        }
        if (newData.vx !== undefined) {
            currentParams.vx = newData.vx;
            vxControl.slider.value = newData.vx.toString();
            vxControl.valueDisplay.textContent = newData.vx.toFixed(0);
        }
        if (newData.vy !== undefined) {
            currentParams.vy = newData.vy;
            vyControl.slider.value = newData.vy.toString();
            vyControl.valueDisplay.textContent = newData.vy.toFixed(0);
        }
        if (newData.spin !== undefined) {
            currentParams.spin = newData.spin;
            spinControl.slider.value = newData.spin.toString();
            spinControl.valueDisplay.textContent = newData.spin.toFixed(2);
        }
        if (newData.tiltX !== undefined) {
            currentParams.tiltX = newData.tiltX;
            tiltControl.slider.value = newData.tiltX.toString();
            tiltControl.valueDisplay.textContent = newData.tiltX.toFixed(0);
        }
    };

    return widget;
}
